import { FC, ComponentProps } from 'react';
import { layoutEntry } from '../shared/layoutEntry';
import { graphql, useRefetchableFragment } from 'react-relay';

// components
import { PdpRespUnavailableContainer } from './PdpRespUnavailableContainer/PdpRespUnavailableContainer';
import { PdpRespUnavailableContextProvider } from './PdpRespUnavailableContext/PdpRespUnavailableContext';

// types
import { PdpRespUnavailableLayoutEntryQuery } from './__generated__/PdpRespUnavailableLayoutEntryQuery.graphql';
import { PdpRespUnavailableLayout_viewer$key as Viewer } from './__generated__/PdpRespUnavailableLayout_viewer.graphql';

const viewerFragment = graphql`
    fragment PdpRespUnavailableLayout_viewer on Viewer
    @refetchable(queryName: "PdpRespUnavailableLayoutRefetchQuery") {
        itemRead(itemIds: $itemIds) {
            ... on Item {
                ...PdpRespUnavailableContainer_item
                ...PdpRespUnavailableContextProvider_item
            }
        }
        user(userId: $userId) @include(if: $fetchUser) {
            ...PdpRespUnavailableContainer_user
        }
        ...PdpRespUnavailableContainer_viewer
    }
`;

const PdpRespUnavailableLayout: FC<{
    viewer: Viewer;
}> = ({ viewer: viewerRef }) => {
    const [viewer, refetch] = useRefetchableFragment(viewerFragment, viewerRef);
    const { user = null, itemRead } = viewer;
    const item = itemRead?.[0];

    if (!item) {
        return null;
    }

    return (
        <PdpRespUnavailableContextProvider item={item} refetch={refetch}>
            <PdpRespUnavailableContainer item={item} user={user} viewer={viewer} />
        </PdpRespUnavailableContextProvider>
    );
};

export const pdpRespUnavailableLayoutEntry = layoutEntry<
    ComponentProps<typeof PdpRespUnavailableLayout>,
    PdpRespUnavailableLayoutEntryQuery
>(
    PdpRespUnavailableLayout,
    graphql`
        query PdpRespUnavailableLayoutEntryQuery(
            $userId: String!
            $itemId: String!
            $isTrade: Boolean!
            $itemIds: [String]!
            $fetchPdp: Boolean!
            $fetchUser: Boolean!
            $fetchChat: Boolean!
            $afterDisplay: Boolean!
            $userZipCode: String = ""
            $forcedCurrency: Currency
            $priceBookName: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $showBuyerChatSuggestions: Boolean = false
        ) {
            viewer {
                ...PdpRespUnavailableLayout_viewer
                ...PdpHeadBuilder_viewer
                itemRead(itemIds: $itemIds) {
                    ...findPdpItemReadError_itemRead
                    ... on Item {
                        ...PdpHeadBuilder_item
                        ...handlePdpStatuses_item @skip(if: $afterDisplay)
                    }
                }
            }
        }
    `
);
